import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { LicenseFeature, getRequiredFeatureForPath, hasLicenseAccess } from '@/lib/utils/licenseConfig';

interface LicenseProtectedRouteProps {
  children: React.ReactNode;
  requiredFeature?: LicenseFeature | null;
}

/**
 * Route guard that checks if the user's company license includes the required feature
 * If not, redirects to the Feature Not Available page
 */
export const LicenseProtectedRoute: React.FC<LicenseProtectedRouteProps> = ({
  children,
  requiredFeature,
}) => {
  const location = useLocation();
  const { selectedCompany, isLoading } = useSimplifiedUserData();
  
  // Determine which feature to check
  // Use explicit requiredFeature prop, or infer from current path
  const featureToCheck = requiredFeature !== undefined 
    ? requiredFeature 
    : getRequiredFeatureForPath(location.pathname);
  
  // If no feature is required (null), always allow access
  if (featureToCheck === null) {
    return <>{children}</>;
  }
  
  // Wait for company data to load before checking
  if (isLoading) {
    return <>{children}</>; // Let the route render while loading (AppLayout has its own loading state)
  }
  
  // Check if the selected company has the required license feature
  const hasAccess = hasLicenseAccess(selectedCompany?.license, featureToCheck);
  
  if (!hasAccess) {
    // Redirect to feature not available page with state
    return (
      <Navigate 
        to="/feature-not-available" 
        replace 
        state={{ 
          featureName: featureToCheck,
          from: location.pathname 
        }} 
      />
    );
  }
  
  return <>{children}</>;
};

